// In-browser stand-in for the Video QC backend, used only while the
// /api/v1/campaigns/:id/qc routes are missing. It implements the same client
// interface as the live one (qcClient.js) and returns the same shapes, so the
// tab is fully clickable for demos and UI work. Everything it returns is made
// up and the tab says so in a banner.
import { QC_STAGES, verdictFor } from './qcFormat';

// Every stage after "Queued", as the QC service reports them.
const STAGES = QC_STAGES.slice(1);
const QUEUE_MS = 800;
const STAGE_MS = 1200;
const RUN_MS = QUEUE_MS + STAGES.length * STAGE_MS;

export const SAMPLE_BRIEF = `Launch reel for the new range. Keep it vertical (9:16), 30-90 seconds.
- Show the product clearly with the brand logo visible
- Ad disclosure on screen: #ad or the Paid partnership label
- Mention the launch offer code
- Do not show or name competitor brands`;

export const SAMPLE_CHECKLIST = [
    { id: 'c1', dimension: 'visual', requirement: 'Product shown clearly with the brand logo visible', critical: true },
    { id: 'c2', dimension: 'visual', requirement: 'Ad disclosure on screen (#ad or Paid partnership label)', critical: true },
    { id: 'c3', dimension: 'spoken', requirement: 'Creator mentions the launch offer code', critical: false },
    { id: 'c4', dimension: 'format', requirement: 'Vertical 9:16, 30-90 seconds long', critical: false },
    { id: 'c5', dimension: 'tone', requirement: 'No competitor brands shown or named', critical: true },
];

const SCENARIOS = ['PASS', 'FAIL', 'NEEDS-REVIEW'];

const mmss = (t) => `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;

const frameUrl = (ts, hue) => `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='180' height='320'><rect width='100%' height='100%' fill='hsl(${hue},40%,85%)'/>`
    + `<text x='50%' y='48%' font-family='sans-serif' font-size='28' font-weight='700' text-anchor='middle' fill='#374151'>${ts}</text>`
    + `<text x='50%' y='58%' font-family='sans-serif' font-size='12' text-anchor='middle' fill='#6b7280'>sample frame</text></svg>`,
)}`;

const sampleFrames = (seed) => Array.from({ length: 20 }, (_, i) => {
    const t = i * 3;
    return { t, ts: mmss(t), url: frameUrl(mmss(t), (seed * 47 + i * 9) % 360) };
});

// Made-up evidence; timestamps match what the text says.
const EVIDENCE = {
    c1: { met: ['Product and logo in frame at 0:06 and 0:24.', ['0:06', '0:24']], missed: ['Logo never readable; product shown side-on only.', ['0:12']] },
    c2: { met: ['"Paid partnership" label visible at 0:00.', ['0:00']], missed: ['No #ad or Paid partnership label in any frame.', []] },
    c3: { met: ['Says "use code LAUNCH10" at 0:41.', ['0:41']], unclear: ['No transcript for this video, and the code is not on screen.', []] },
    c4: { met: ['1080x1920, 0:57 long.', []] },
    c5: { met: ['No other brands seen or heard.', []] },
};

function resultsFor(checklist, scenario) {
    return checklist.map((item) => {
        let status = 'met';
        if (scenario === 'FAIL' && item.id === 'c2') status = 'missed';
        if (scenario === 'NEEDS-REVIEW' && item.id === 'c3') status = 'unclear';
        const [text, timestamps] = EVIDENCE[item.id]?.[status]
            || [status === 'met' ? 'Seen in the sampled frames.' : 'Could not confirm from the frames.', status === 'met' ? ['0:06'] : []];
        return { item_id: item.id, dimension: item.dimension, requirement: item.requirement, critical: item.critical, status, evidence: text, timestamps };
    });
}

const OUTLIERS = {
    PASS: [],
    FAIL: [{ ts: '0:18', severity: 'low', note: 'Glare on the bottle makes the label hard to read for about 2 seconds.' }],
    'NEEDS-REVIEW': [{ ts: '0:33', severity: 'medium', note: 'Background music drowns out speech for about 5 seconds.' }],
};

const FRAME_FEEDBACK = {
    PASS: [
        { ts: '0:00', status: 'good', note: 'Paid partnership label visible from the first frame.' },
        { ts: '0:06', status: 'good', note: 'Product and logo clearly visible.' },
    ],
    FAIL: [
        { ts: '0:00', status: 'bad', note: 'No disclosure label in the opening frame.' },
        { ts: '0:06', status: 'good', note: 'Product and logo clearly visible.' },
        { ts: '0:18', status: 'warn', note: 'Glare on the label.' },
    ],
    'NEEDS-REVIEW': [
        { ts: '0:06', status: 'good', note: 'Product and logo clearly visible.' },
        { ts: '0:33', status: 'warn', note: 'Music level spikes here.' },
    ],
};

// Made-up speech. The needs-review sample has none, which is why its offer
// code is "unclear".
const TRANSCRIPT = {
    segments: [
        { start: 0, end: 5, text: 'Okay, festive glow routine, let’s go.' },
        { start: 6, end: 14, text: 'This is the new serum. Two drops, every night.' },
        { start: 24, end: 31, text: 'A week in and my skin feels a lot brighter.' },
        { start: 41, end: 47, text: 'Use code LAUNCH10 for ten percent off the launch.' },
    ],
};

function makeCheck(store, scenario, now) {
    store.seq += 1;
    const results = resultsFor(store.checklist, scenario);
    const counts = { met: 0, missed: 0, unclear: 0 };
    results.forEach((r) => { counts[r.status] += 1; });
    const outliers = OUTLIERS[scenario];
    return {
        id: `sample-check-${store.seq}`,
        kind: 'ai',
        verdict: verdictFor(results),
        summary: {
            PASS: 'Every requirement is visible in the video. Safe to approve.',
            FAIL: 'The ad disclosure is missing, which fails a critical requirement.',
            'NEEDS-REVIEW': 'Nothing fails outright, but the offer code could not be confirmed.',
        }[scenario],
        results,
        outliers,
        frame_feedback: FRAME_FEEDBACK[scenario],
        counts: { ...counts, outliers: outliers.length },
        model: 'sample',
        decided_by: null,
        decision_note: null,
        created_at: new Date(now).toISOString(),
    };
}

function newVideo(store, { source, title, external_ref }, now) {
    store.seq += 1;
    return {
        id: `sample-video-${store.seq}`, external_ref: external_ref || null, source, title: title || 'Untitled video',
        status: 'pending', stage: '', error: null, attempts: 0, format_meta: null, frames: [], transcript: null,
        checks: [], runStartedAt: null, created_at: new Date(now).toISOString(),
    };
}

const startRun = (video, now) => ({ ...video, status: 'queued', stage: 'Queued', error: null, runStartedAt: now, attempts: 1 });

// Returns the video moved along its stages by elapsed time, completed once
// the run time has passed. Videos are replaced, never edited in place.
function advance(store, video, now) {
    if (video.runStartedAt == null) return video;
    const elapsed = now - video.runStartedAt;
    if (elapsed < QUEUE_MS) return video;
    if (elapsed < RUN_MS) return { ...video, status: 'processing', stage: STAGES[Math.floor((elapsed - QUEUE_MS) / STAGE_MS)] };
    const scenario = SCENARIOS[store.runs % SCENARIOS.length];
    store.runs += 1;
    const check = makeCheck(store, scenario, now);
    return {
        ...video, status: 'done', stage: 'Done', runStartedAt: null, frames: sampleFrames(store.seq),
        format_meta: { duration: '0:57', resolution: '1080x1920', aspect_ratio: '9:16', has_audio: true },
        transcript: scenario === 'NEEDS-REVIEW' ? null : TRANSCRIPT,
        checks: [check, ...video.checks],
    };
}

const summary = (video) => {
    const latest = video.checks[0];
    return {
        id: video.id, external_ref: video.external_ref, source: video.source, title: video.title,
        status: video.status, stage: video.stage, error: video.error, attempts: video.attempts,
        latest_check: latest ? { id: latest.id, kind: latest.kind, verdict: latest.verdict, created_at: latest.created_at, counts: latest.counts } : null,
        created_at: video.created_at,
    };
};

const detail = (video) => {
    const { checks, runStartedAt, ...rest } = video;
    return { ...rest, latest_check: checks[0] || null };
};

function guessDimension(line) {
    if (/\b(say|says|mention|speak|voice|call out)\b/i.test(line)) return 'spoken';
    if (/\b(9:16|16:9|seconds|duration|resolution|caption|subtitle|vertical|length)\b/i.test(line)) return 'format';
    if (/\b(tone|don't|do not|avoid|never|style)\b/i.test(line)) return 'tone';
    return 'visual';
}

export function draftChecklistFromBrief(brief) {
    return brief.split('\n')
        .map((line) => line.replace(/^[\s\-*•\d.)]+/, '').trim())
        .filter((line) => line.length > 3)
        .slice(0, 30)
        .map((requirement, i) => ({
            id: `c${i + 1}`,
            dimension: guessDimension(requirement),
            requirement,
            critical: /\b(must|mandatory|disclos|#ad|legal|do not|never)\b/i.test(requirement),
        }));
}

function seed(store, campaignVideos, now) {
    const pick = campaignVideos.slice(0, 3);
    const sources = pick.length ? pick.map((v) => ({
        source: v.source || (v.youtubeVideoId ? `https://www.youtube.com/watch?v=${v.youtubeVideoId}` : 'https://youtu.be/sample'),
        title: v.title, external_ref: String(v.id),
    })) : [{ source: 'sample-upload://reel-v1.mp4', title: 'Creator draft - reel v1' }, { source: 'sample-upload://reel-v2.mp4', title: 'Creator draft - reel v2' }];
    sources.forEach((s) => {
        const finished = advance(store, startRun(newVideo(store, s, now), now - RUN_MS), now);
        store.videos.set(finished.id, finished);
    });
}

const stores = new Map();

// One sample store per campaign, kept for the browser session so switching
// tabs doesn't reset what you clicked.
export function createSampleQc(campaignId, campaignVideos = [], { now = () => Date.now(), fresh = false } = {}) {
    if (fresh || !stores.has(campaignId)) {
        const store = { brief: SAMPLE_BRIEF, checklist: SAMPLE_CHECKLIST, videos: new Map(), seq: 0, runs: 0 };
        seed(store, campaignVideos, now());
        stores.set(campaignId, store);
    }
    const store = stores.get(campaignId);
    const put = (video) => { store.videos.set(video.id, video); return video; };
    const find = (id) => {
        const video = store.videos.get(id);
        if (!video) throw new Error('Video not found');
        return put(advance(store, video, now()));
    };

    return {
        mode: 'sample',
        async overview() {
            [...store.videos.values()].forEach((v) => put(advance(store, v, now())));
            return { brief: store.brief, checklist: store.checklist, videos: [...store.videos.values()].map(summary) };
        },
        async saveBrief({ brief, checklist }) {
            store.brief = brief;
            store.checklist = checklist;
            return { brief, checklist };
        },
        async extractChecklist() {
            if (!store.brief.trim()) throw new Error('Add a brief first');
            store.checklist = draftChecklistFromBrief(store.brief);
            return { brief: store.brief, checklist: store.checklist };
        },
        createVideo: async (input) => detail(put(startRun(newVideo(store, input, now()), now()))),
        getVideo: async (id) => detail(find(id)),
        getHistory: async (id) => find(id).checks,
        async runQc(id) {
            const video = find(id);
            if (video.status === 'queued' || video.status === 'processing') throw new Error('QC is already running for this video');
            return detail(put(startRun(video, now())));
        },
        async decide(id, { verdict, note, check_id: checkId }) {
            const video = find(id);
            const last = video.checks[0];
            if (checkId && last && last.id !== checkId) {
                return Promise.reject({ success: false, error: { code: 'stale_result', message: 'A newer result exists. Reload and review it.' } });
            }
            store.seq += 1;
            const check = {
                ...(last || { results: [], outliers: [], frame_feedback: [], summary: '' }),
                id: `sample-check-${store.seq}`, kind: 'human', verdict, decided_by: 'You (sample)', decision_note: note,
                created_at: new Date(now()).toISOString(),
            };
            put({ ...video, checks: [check, ...video.checks] });
            return check;
        },
        upload: async (file) => `sample-upload://${file.name}`,
    };
}

export const SAMPLE_RUN_MS = RUN_MS;
